import * as math from "mathjs";

/**
 * Converts image coordinates to map coordinates
 * @param extent the image extent coordinates
 * @param anchor the coordinates of the points representing the corners on the map
 * @param coordinates coordinates of the image
 * @retTopRightns coordinates of the map
 */
export function ReverseTransform(extent: number[], anchor: number[][], coordinates: number[]): number[] {

    // Compute the image coordinates
    const imageLowerLeft = [extent[0], extent[1]];
    const imageLowerRight = [extent[2], extent[1]];
    const imageTopLeft = [extent[0], extent[3]];
    const imageTopRight = [extent[2], extent[3]];

    // Compute the anchor coordinates
    const anTopLeft = anchor[1];
    const anTopRight = anchor[2];
    const anLowerRight = anchor[3];
    const anLowerLeft = anchor[0];

    // Compute the reverse transformation matrix
    const matrix = calcTopLeftateHomography(imageLowerLeft, anLowerLeft, imageLowerRight, anLowerRight, imageTopLeft, anTopLeft, imageTopRight, anTopRight);

    // Perform the transformation and return result
    const reverse = applyHomography(matrix, coordinates);

    return reverse;
}

/**
 * Convert map coordinates into image coordinates
 * @param extent the image extent coordinates
 * @param anchor the coordinates of the points representing the corners on the map
 * @param coordinates coordinates of the image
 * @retTopRightns coordinates of the image
 */
export function ForwardTransform(extent: number[], anchor: number[][], coordinates: number[]): number[] {
    // Compute the image coordinates
    const imageLowerLeft = [extent[0], extent[1]];
    const imageLowerRight = [extent[2], extent[1]];
    const imageTopLeft = [extent[0], extent[3]];
    const imageTopRight = [extent[2], extent[3]];

    // Compute the anchor coordinates
    const anTopLeft = anchor[1];
    const anTopRight = anchor[2];
    const anLowerRight = anchor[3];
    const anLowerLeft = anchor[0];

    // Compute the reverse transformation matrix
    const matrix = calcTopLeftateHomography(anLowerLeft, imageLowerLeft, anLowerRight, imageLowerRight, anTopLeft, imageTopLeft, anTopRight, imageTopRight);

    // Perform the transformation and return result
    const forward = applyHomography(matrix, coordinates);
    
    return forward;
}

/**
 * Compute a homography matrix for the projective transformation
 * @param a1 source 1
 * @param b1 target 1
 * @param a2 source 2
 * @param b2 target 2
 * @param a3 source 3
 * @param b3 target 3
 * @param a4 source 4
 * @param b4 target 4
 */
function calcTopLeftateHomography(a1: number[], b1: number[], a2: number[], b2: number[], a3: number[], b3: number[], a4: number[], b4: number[]): math.Matrix {

    let points = math.matrix([
        [-a1[0], -a1[1], -1, 0, 0, 0, a1[0] * b1[0], a1[1] * b1[0], b1[0]],
        [0, 0, 0, -a1[0], -a1[1], -1, a1[0] * b1[1], a1[1] * b1[1], b1[1]],
        [-a2[0], -a2[1], -1, 0, 0, 0, a2[0] * b2[0], a2[1] * b2[0], b2[0]],
        [0, 0, 0, -a2[0], -a2[1], -1, a2[0] * b2[1], a2[1] * b2[1], b2[1]],
        [-a3[0], -a3[1], -1, 0, 0, 0, a3[0] * b3[0], a3[1] * b3[0], b3[0]],
        [0, 0, 0, -a3[0], -a3[1], -1, a3[0] * b3[1], a3[1] * b3[1], b3[1]],
        [-a4[0], -a4[1], -1, 0, 0, 0, a4[0] * b4[0], a4[1] * b4[0], b4[0]],
        [0, 0, 0, -a4[0], -a4[1], -1, a4[0] * b4[1], a4[1] * b4[1], b4[1]],
        [0, 0, 0, 0, 0, 0, 0, 0, 1]
    ])

    let pointsInv = math.inv(points);
    let hnum = (math.multiply(pointsInv, [0, 0, 0, 0, 0, 0, 0, 0, 1]));
    return math.reshape(hnum, [3, 3]);
}

/**
 * Convert the x/y coordinates with the homography matrix
 * @param homography homography matrix
 * @param x soTopRightce X
 * @param y soTopRightce Y
 */
function applyHomography(homography: math.Matrix, coordinates: number[]): number[] {

    // Perform matrix computation
    const B = math.multiply(homography, [coordinates[0], coordinates[1], 1]);
    const outX = B.get([0]) / B.get([2]);
    const outY = B.get([1]) / B.get([2]);

    return [outX, outY];
}